import {
  default as React,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { makeStyles } from '@mui/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import PersonIcon from '@mui/icons-material/Person';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  useApi,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { useCurrentUserEmail } from '../components/catalog/microapps/hooks';

interface OktaUser {
  id: string;
  profile: {
    login: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName?: string;
  };
}

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(1),
  },
  emailChip: {
    height: '28px',
    fontSize: '0.85rem',
    fontWeight: 500,
    backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#e0e0e0',
    color: theme.palette.mode === 'dark' ? '#fff' : '#424242',
    border: `1px solid ${
      theme.palette.mode === 'dark' ? '#616161' : '#bdbdbd'
    }`,
    '& .MuiChip-deleteIcon': {
      color:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.7)'
          : 'rgba(0, 0, 0, 0.6)',
      '&:hover': {
        color: theme.palette.mode === 'dark' ? '#fff' : '#000',
      },
    },
  },
  helperText: {
    marginTop: theme.spacing(1),
    fontSize: '0.875rem',
  },
  userCount: {
    marginTop: theme.spacing(0.5),
    fontSize: '0.75rem',
  },
}));

/**
 * Scaffolder field extension for selecting allowed user email addresses from Okta.
 * Used when "Custom (Specific Users)" visibility is selected in the deploy form.
 *
 * Searches Okta via GET /api/microapps-api/access/users/search?q=...
 * Returns: string[] (array of email addresses)
 *
 * Usage in template YAML:
 *   ui:field: AccessControlField
 */
export const AccessControlField = ({
  onChange,
  rawErrors,
  required,
  formData,
  schema,
  formContext,
}: FieldExtensionComponentProps<string[]>) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);

  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<OktaUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);

  const emails = useMemo<string[]>(
    () => (Array.isArray(formData) ? formData : []),
    [formData],
  );

  /** Owner/creator emails pre-filled by the redeploy flow — cannot be removed. */
  const ownerEmails = useMemo<string[]>(
    () =>
      Array.isArray(formContext?.formData?.ownerEmails)
        ? (formContext.formData.ownerEmails as string[]).map((e: string) =>
            e.toLowerCase(),
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formContext?.formData?.ownerEmails],
  );

  /** Current logged-in user — always shown as a protected owner chip. */
  const currentUserEmail = useCurrentUserEmail();

  /**
   * On first resolution of currentUserEmail, seed it into the field list so
   * it shows up as a chip immediately (fresh deploy). On redeploy it is already
   * present in the pre-filled allowedEmails, so the guard prevents duplication.
   */
  const initializedRef = useRef(false);
  const emailsRef = useRef(emails);
  emailsRef.current = emails;

  useEffect(() => {
    if (!currentUserEmail || initializedRef.current) return;
    initializedRef.current = true;
    if (!emailsRef.current.includes(currentUserEmail)) {
      onChange([currentUserEmail, ...emailsRef.current]);
    }
  }, [currentUserEmail, onChange]);

  /**
   * Effective protected set:
   *  - Redeploy: ownerEmails from formContext (pre-filled from backstage.yaml annotations)
   *  - Fresh deploy: ownerEmails is empty, so protect the current logged-in user's email
   */
  const effectiveOwnerEmails = useMemo<Set<string>>(() => {
    if (ownerEmails.length > 0) return new Set(ownerEmails);
    // Lowercase currentUserEmail to match the .toLowerCase() check in the chip render.
    // Without this, a mixed-case email from the JWT (e.g. "User@Domain.com") won't match
    // the Set lookup done via email.toLowerCase(), causing isOwner to be false and showing
    // a delete button on the owner chip.
    if (currentUserEmail) return new Set([currentUserEmail.toLowerCase()]);
    return new Set<string>();
  }, [ownerEmails, currentUserEmail]);

  const fieldTitle = schema?.title ?? 'Allowed Users';
  const backendUrl = config.getString('backend.baseUrl');

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const searchUsers = useCallback(
    async (query: string) => {
      if (query.trim().length < 2) {
        setOptions([]);
        return;
      }
      setLoading(true);
      setFetchError(null);
      try {
        const { token } = await identityApi.getCredentials();
        const res = await fetch(
          `${backendUrl}/api/microapps-api/access/users/search?q=${encodeURIComponent(
            query.trim(),
          )}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
          },
        );
        if (!res.ok) {
          throw new Error(`Search failed: ${res.status}`);
        }
        const data = (await res.json()) as { users: OktaUser[] };
        // Filter out already-selected users
        setOptions(data.users.filter(u => !emails.includes(u.profile.email)));
      } catch (_err) {
        setFetchError('Failed to search users. Try again.');
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [backendUrl, identityApi, emails],
  );

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      void searchUsers(inputValue);
    }, 300);
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [inputValue, searchUsers]);

  const handleSelect = useCallback(
    (_: React.SyntheticEvent, user: OktaUser | null) => {
      if (!user) return;
      const email = user.profile.email;
      if (!emails.includes(email)) {
        onChange([...emails, email]);
      }
      setInputValue('');
      setOptions([]);
    },
    [emails, onChange],
  );

  const removeEmail = useCallback(
    (emailToRemove: string) => {
      if (effectiveOwnerEmails.has(emailToRemove.toLowerCase())) return;
      onChange(emails.filter(e => e !== emailToRemove));
    },
    [emails, onChange, effectiveOwnerEmails],
  );

  const userCountLabel =
    emails.length === 1 ? '1 user added' : `${emails.length} users added`;

  const hasError = rawErrors !== undefined && rawErrors.length > 0;

  const noOptionsText =
    inputValue.trim().length < 2
      ? 'Type at least 2 characters to search'
      : fetchError ?? 'No users found';

  return (
    <FormControl fullWidth className={classes.root} error={hasError}>
      {emails.length > 0 && (
        <Box className={classes.chips}>
          {emails.map(email => {
            const isOwner = effectiveOwnerEmails.has(email.toLowerCase());
            return (
              <Chip
                key={email}
                label={email}
                onDelete={isOwner ? undefined : () => removeEmail(email)}
                className={classes.emailChip}
                size="small"
                title={isOwner ? 'Owner — cannot be removed' : undefined}
              />
            );
          })}
        </Box>
      )}

      <Autocomplete
        options={options}
        getOptionLabel={(option: OktaUser) => {
          const { firstName, lastName, email } = option.profile;
          return `${firstName} ${lastName} (${email})`;
        }}
        isOptionEqualToValue={(option, value) =>
          option.profile.email === value.profile.email
        }
        inputValue={inputValue}
        onInputChange={(_, val, reason) => {
          if (reason !== 'reset') {
            setInputValue(val);
          }
        }}
        onChange={handleSelect}
        value={null}
        loading={loading}
        filterOptions={x => x}
        noOptionsText={noOptionsText}
        renderInput={params => (
          <TextField
            {...params}
            label={fieldTitle}
            required={required && emails.length === 0}
            error={hasError}
            placeholder="Search by name or email..."
            InputLabelProps={{ shrink: true }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading && <CircularProgress size={18} />}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option.profile.email}>
            <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.6 }} />
            <Box>
              <Typography variant="body2">
                {option.profile.firstName} {option.profile.lastName}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {option.profile.email}
              </Typography>
            </Box>
          </li>
        )}
      />

      {hasError && (
        <FormHelperText error>
          {required && emails.length === 0
            ? 'At least one user is required'
            : rawErrors?.join(', ')}
        </FormHelperText>
      )}

      <FormHelperText className={classes.helperText}>
        {schema?.description ??
          'Add users who should have access at deployment time. You are always included automatically.'}
      </FormHelperText>

      {emails.length > 0 && (
        <Typography
          variant="caption"
          color="textSecondary"
          className={classes.userCount}
        >
          {userCountLabel}
        </Typography>
      )}
    </FormControl>
  );
};

export const AccessControlFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'AccessControlField',
    component: AccessControlField,
  }),
);
